import React, {useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
// react-quill-new, not react-quill: the original was last published in 2023 and
// pins quill 1.3.7, which carries an unpatched XSS (GHSA-4943-9vgg-gr5r). This
// fork tracks quill 2.x and keeps the same component API.
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import '../styles/quill-custom.css'
import { API_URL } from '../config'
import {
  PlusIcon,
  DocumentTextIcon,
  DocumentIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

interface KnowledgeItem {
  _id: string
  title: string
  content: string
  type: 'txt' | 'pdf' | 'url'
  source: string
  status: string
  tags: string[]
  isActive: boolean
  metadata: {
    wordCount?: number
    lastUpdated?: string
  }
  createdAt: string
  updatedAt: string
}

const Knowledge: React.FC = () => {
  const [knowledge, setKnowledge] = useState<KnowledgeItem[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [addMode, setAddMode] = useState<'text' | 'file' | 'url'>('text')

  // Form states
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [url, setUrl] = useState('')
  const [tags, setTags] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // The list endpoint returns 10 items per page by default. This page used to
  // request no parameters and render whatever came back, so once the knowledge
  // base grew past 10 items the rest were simply unreachable — and the search
  // box only filtered that one loaded page.
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 })

  // Filters on isActive, not status, because that is what the row badge and
  // the Activate/Deactivate button read.
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all')

  /**
   * Totals for the summary cards, fetched separately.
   *
   * They previously counted the loaded array, so after paginating they showed
   * the page size — "Total Items 20" above a footer reading "185 items". The
   * list endpoint only knows about the current page, so these come from
   * /knowledge/stats/overview, which aggregates across everything.
   */
  const [stats, setStats] = useState({ total: 0, active: 0, textFiles: 0, urls: 0 })

  useEffect(() => {
    fetchKnowledge()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, activeFilter])

  useEffect(() => {
    fetchStats()
  }, [])

  // Search runs on the server so it covers every item, not just the current
  // page. Debounced so each keystroke isn't a request.
  //
  // The ref skips the first run: the [page] effect above already fetches on
  // mount, and without this the page would issue two identical requests every
  // time it loads.
  const searchInitialised = useRef(false)
  useEffect(() => {
    if (!searchInitialised.current) {
      searchInitialised.current = true
      return
    }
    const t = setTimeout(() => {
      if (page !== 1) setPage(1)
      else fetchKnowledge()
    }, 350)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const fetchKnowledge = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/knowledge`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          limit: pageSize,
          ...(search.trim() ? { search: search.trim() } : {}),
          ...(activeFilter !== 'all' ? { isActive: activeFilter === 'active' } : {}),
        },
      })
      setKnowledge(response.data.knowledge)
      if (response.data.pagination) setPagination(response.data.pagination)
    } catch (error) {
      console.error('Error fetching knowledge:', error)
      toast.error('Failed to load knowledge base')
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token')
      const { data } = await axios.get(`${API_URL}/knowledge/stats/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setStats({
        total: data.total ?? 0,
        active: data.byStatus?.active ?? 0,
        // "Text Files" covers both uploaded formats — pdf items are files too.
        textFiles: (data.byType?.txt ?? 0) + (data.byType?.pdf ?? 0),
        urls: data.byType?.url ?? 0,
      })
    } catch (error) {
      console.error('Error fetching knowledge stats:', error)
    }
  }

  // Helper function to convert HTML to plain text
  const htmlToText = (html: string): string => {
    const temp = document.createElement('div')
    temp.innerHTML = html
    return temp.textContent || temp.innerText || ''
  }

  const handleAddText = async () => {
    const plainTextContent = htmlToText(content)

    if (!title.trim() || !plainTextContent.trim()) {
      toast.error('Title and content are required')
      return
    }

    setSubmitting(true)
    try {
      const token = localStorage.getItem('token')

      // Create a text file from content (plain text)
      const blob = new Blob([plainTextContent], { type: 'text/plain' })
      const formData = new FormData()
      formData.append('file', blob, 'content.txt')
      formData.append('title', title)
      formData.append('tags', tags)

      await axios.post(`${API_URL}/knowledge/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      })

      toast.success('Knowledge added successfully')
      resetForm()
      setShowAddModal(false)
      fetchKnowledge()
      fetchStats()
    } catch (error: any) {
      console.error('Error adding knowledge:', error)
      toast.error(error.response?.data?.error || 'Failed to add knowledge')
    } finally {
      setSubmitting(false)
    }
  }

  const handleAddFile = async () => {
    if (!file) {
      toast.error('Please select a file')
      return
    }

    setSubmitting(true)
    try {
      const token = localStorage.getItem('token')
      const formData = new FormData()
      formData.append('file', file)
      if (title) formData.append('title', title)
      if (tags) formData.append('tags', tags)

      await axios.post(`${API_URL}/knowledge/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      })

      toast.success('File uploaded successfully')
      resetForm()
      setShowAddModal(false)
      fetchKnowledge()
      fetchStats()
    } catch (error: any) {
      console.error('Error uploading file:', error)
      toast.error(error.response?.data?.error || 'Failed to upload file')
    } finally {
      setSubmitting(false)
    }
  }

  const handleAddUrl = async () => {
    if (!url.trim()) {
      toast.error('URL is required')
      return
    }

    setSubmitting(true)
    try {
      const token = localStorage.getItem('token')
      await axios.post(
        `${API_URL}/knowledge/url`,
        {
          url: url.trim(),
          // Omitted when blank rather than sent as '', so the backend applies
          // the scraped page title — which is what the field promises.
          ...(title.trim() ? { title: title.trim() } : {}),
          tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      toast.success('URL content added successfully')
      resetForm()
      setShowAddModal(false)
      fetchKnowledge()
      fetchStats()
    } catch (error: any) {
      console.error('Error adding URL:', error)
      toast.error(error.response?.data?.error || 'Failed to add URL')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this knowledge item?')) return

    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/knowledge/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      toast.success('Knowledge deleted successfully')
      fetchKnowledge()
      fetchStats()
    } catch (error) {
      console.error('Error deleting knowledge:', error)
      toast.error('Failed to delete knowledge')
    }
  }

  const handleToggleActive = async (item: KnowledgeItem) => {
    try {
      const token = localStorage.getItem('token')
      await axios.put(
        `${API_URL}/knowledge/${item._id}`,
        { isActive: !item.isActive },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      toast.success(`Knowledge ${!item.isActive ? 'activated' : 'deactivated'}`)
      fetchKnowledge()
      fetchStats()
    } catch (error) {
      console.error('Error updating knowledge:', error)
      toast.error('Failed to update knowledge')
    }
  }

  const resetForm = () => {
    setTitle('')
    setContent('')
    setUrl('')
    setTags('')
    setFile(null)
  }

  // Filtering happens server-side now (see fetchKnowledge), so the list is
  // rendered as returned. Filtering again here would hide valid results,
  // because the server matches full content while this only saw title/tags.
  const filteredKnowledge = knowledge

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return <DocumentIcon className="h-5 w-5 text-red-500" />
      case 'url':
        return <GlobeAltIcon className="h-5 w-5 text-primary" />
      default:
        return <DocumentTextIcon className="h-5 w-5 text-muted-foreground" />
    }
  }

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Knowledge Base</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your chatbot's knowledge and training data
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Knowledge
        </button>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-muted-foreground/70" />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-input rounded-md leading-5 bg-card placeholder:text-muted-foreground focus:outline-none focus:ring-ring focus:border-primary"
            placeholder="Search knowledge..."
          />
        </div>

        <div className="flex items-center gap-2">
          {(['all', 'active', 'inactive'] as const).map((value) => {
            const label = { all: 'All', active: 'Active', inactive: 'Deactivated' }[value]
            const isSelected = activeFilter === value
            return (
              <button
                key={value}
                type="button"
                aria-pressed={isSelected}
                onClick={() => {
                  setActiveFilter(value)
                  // A filter change makes the current page number meaningless.
                  setPage(1)
                }}
                className={
                  isSelected
                    ? 'inline-flex h-9 items-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground shadow-sm'
                    : 'inline-flex h-9 items-center rounded-md border border-input bg-card px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground'
                }
              >
                {label}
                {value === 'active' && stats.total > 0 && (
                  <span className="ml-1.5 text-xs opacity-70">{stats.active}</span>
                )}
                {value === 'inactive' && stats.total > 0 && (
                  <span className="ml-1.5 text-xs opacity-70">{stats.total - stats.active}</span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-muted-foreground">Total Items</div>
          <div className="text-2xl font-bold text-foreground">{stats.total}</div>
        </div>
        <div className="bg-card p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-muted-foreground">Active</div>
          <div className="text-2xl font-bold text-green-600">
            {stats.active}
          </div>
        </div>
        <div className="bg-card p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-muted-foreground">Text Files</div>
          <div className="text-2xl font-bold text-foreground">
            {stats.textFiles}
          </div>
        </div>
        <div className="bg-card p-4 rounded-lg shadow">
          <div className="text-sm font-medium text-muted-foreground">URLs</div>
          <div className="text-2xl font-bold text-primary">
            {stats.urls}
          </div>
        </div>
      </div>

      {/* Knowledge List */}
      <div className="bg-card shadow overflow-hidden rounded-lg">
        {filteredKnowledge.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-muted-foreground/70" />
            <h3 className="mt-2 text-sm font-medium text-foreground">No knowledge items</h3>
            <p className="mt-1 text-sm text-muted-foreground">Get started by adding your first knowledge item.</p>
            <div className="mt-6">
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Knowledge
              </button>
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {filteredKnowledge.map((item) => (
              <li key={item._id} className="p-6 hover:bg-muted/50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="flex-shrink-0 mt-1">
                      {getTypeIcon(item.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-sm font-medium truncate">
                          <Link
                            to={`/knowledge/${item._id}`}
                            className="text-foreground underline-offset-4 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                          >
                            {item.title}
                          </Link>
                        </h3>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            item.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-muted text-foreground'
                          }`}
                        >
                          {item.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                        {item.content.substring(0, 150)}...
                      </p>
                      <div className="mt-2 flex items-center space-x-4 text-xs text-muted-foreground">
                        <span>Type: {item.type.toUpperCase()}</span>
                        {item.metadata.wordCount && (
                          <span>Words: {item.metadata.wordCount}</span>
                        )}
                        <span>
                          Created: {new Date(item.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      {item.tags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {item.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleToggleActive(item)}
                      className={`inline-flex items-center px-3 py-1 border border-input rounded-md text-sm font-medium ${
                        item.isActive
                          ? 'text-foreground bg-card hover:bg-muted/50'
                          : 'text-green-700 bg-green-50 hover:bg-green-100'
                      }`}
                    >
                      {item.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    {/* Edits happen on the detail page, where the source and
                        dates are visible alongside the content being changed. */}
                    <Link
                      to={`/knowledge/${item._id}?edit=1`}
                      className="p-2 text-muted-foreground/70 hover:text-primary rounded-full hover:bg-accent"
                      title="Edit"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </Link>
                    {/* Opens the detail page rather than a modal: the content
                        can run to thousands of words, and a page gives it room,
                        a shareable URL and working browser navigation. */}
                    <Link
                      to={`/knowledge/${item._id}`}
                      className="p-2 text-muted-foreground/70 hover:text-foreground rounded-full hover:bg-accent"
                      title="View details"
                    >
                      <DocumentTextIcon className="h-5 w-5" />
                    </Link>
                    <button
                      onClick={() => handleDelete(item._id)}
                      className="p-2 text-red-400 hover:text-red-600 rounded-full hover:bg-red-50"
                      title="Delete"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {pagination.total > 0 && (
          <div className="flex flex-col gap-3 border-t border-border px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <p className="text-sm text-muted-foreground">
                Showing{' '}
                <span className="font-medium text-foreground">
                  {(pagination.page - 1) * pagination.limit + 1}
                </span>
                –
                <span className="font-medium text-foreground">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-medium text-foreground">{pagination.total}</span>
                {search.trim() ? ' matching items' : ' items'}
              </p>

              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="hidden sm:inline">Per page</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value))
                    // Page 4 of 10 has no meaning once the page size changes.
                    setPage(1)
                  }}
                  className="input-field h-8 w-auto py-0 text-sm"
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(1)}
                disabled={pagination.page <= 1}
                className="btn-outline btn-sm"
                aria-label="First page"
              >
                First
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pagination.page <= 1}
                className="btn-outline btn-sm"
              >
                Previous
              </button>
              <span className="px-1 text-sm tabular-nums text-muted-foreground">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                disabled={pagination.page >= pagination.pages}
                className="btn-outline btn-sm"
              >
                Next
              </button>
              <button
                type="button"
                onClick={() => setPage(pagination.pages)}
                disabled={pagination.page >= pagination.pages}
                className="btn-outline btn-sm"
                aria-label="Last page"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-foreground/40 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-medium text-foreground">Add Knowledge</h3>
              <button
                onClick={() => {
                  setShowAddModal(false)
                  resetForm()
                }}
                className="text-muted-foreground/70 hover:text-muted-foreground"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              {/* Mode Selection */}
              <div className="flex space-x-2 mb-6">
                <button
                  onClick={() => setAddMode('text')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium ${
                    addMode === 'text'
                      ? 'bg-primary text-white'
                      : 'bg-muted text-foreground hover:bg-accent'
                  }`}
                >
                  <DocumentTextIcon className="h-5 w-5 inline mr-2" />
                  Text
                </button>
                <button
                  onClick={() => setAddMode('file')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium ${
                    addMode === 'file'
                      ? 'bg-primary text-white'
                      : 'bg-muted text-foreground hover:bg-accent'
                  }`}
                >
                  <DocumentIcon className="h-5 w-5 inline mr-2" />
                  File
                </button>
                <button
                  onClick={() => setAddMode('url')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium ${
                    addMode === 'url'
                      ? 'bg-primary text-white'
                      : 'bg-muted text-foreground hover:bg-accent'
                  }`}
                >
                  <GlobeAltIcon className="h-5 w-5 inline mr-2" />
                  URL
                </button>
              </div>

              {/* Text Mode */}
              {addMode === 'text' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="mt-1 block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                      placeholder="Enter title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Content <span className="text-red-500">*</span>
                    </label>
                    <div className="border border-input rounded-md">
                      <ReactQuill
                        theme="snow"
                        value={content}
                        onChange={setContent}
                        placeholder="Enter knowledge content... Use the toolbar to format your text."
                        modules={{
                          toolbar: [
                            [{ 'header': [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                            [{ 'indent': '-1'}, { 'indent': '+1' }],
                            ['link'],
                            [{ 'align': [] }],
                            ['clean']
                          ],
                        }}
                        formats={[
                          'header',
                          'bold', 'italic', 'underline', 'strike',
                          'list', 'bullet', 'indent',
                          'link', 'align'
                        ]}
                        style={{ height: '300px', marginBottom: '50px' }}
                      />
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      💡 Format your content with headings, lists, bold, italic, and more. The text will be saved as plain text for the chatbot.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Tags (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      className="mt-1 block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                      placeholder="e.g. support, faq, pricing"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Tags help categorize and search your knowledge base
                    </p>
                  </div>
                </div>
              )}

              {/* File Mode */}
              {addMode === 'file' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground">
                      File (TXT or PDF)
                    </label>
                    <input
                      type="file"
                      accept=".txt,.pdf"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                      className="mt-1 block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground">
                      Title (optional)
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="mt-1 block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                      placeholder="Leave empty to use filename"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground">
                      Tags (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      className="mt-1 block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                      placeholder="e.g. manual, guide, documentation"
                    />
                  </div>
                </div>
              )}

              {/* URL Mode */}
              {addMode === 'url' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground">URL</label>
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      className="mt-1 block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                      placeholder="https://example.com/page"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground">
                      Title (optional)
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="mt-1 block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                      placeholder="Leave empty to use page title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground">
                      Tags (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      className="mt-1 block w-full border border-input rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-ring focus:border-primary"
                      placeholder="e.g. blog, article, news"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 px-6 py-4 bg-muted/50 border-t border-border">
              <button
                onClick={() => {
                  setShowAddModal(false)
                  resetForm()
                }}
                className="px-4 py-2 border border-input rounded-md text-sm font-medium text-foreground hover:bg-muted/50"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (addMode === 'text') handleAddText()
                  else if (addMode === 'file') handleAddFile()
                  else handleAddUrl()
                }}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50"
                disabled={submitting}
              >
                {submitting ? 'Adding...' : 'Add Knowledge'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
    </div>
  )
}

export default Knowledge
